/* Ombrance — atelier-studio.js : l'Atelier guidé (<atelier-studio>) et son bouton fixe mobile (<atelier-sticky>).
   Choix d'un univers, description, options (pré-remplissage ?style=…&idee=…) ; coût annoncé avant l'envoi ; génération
   (Idempotency-Key) puis attente par interrogation, reprise après actualisation ; résultat en N propositions (groupe de
   boutons radio), affinage d'une proposition, variantes, favoris, téléchargement ; crédits et conservation des créations
   offertes par un compte Shopify. Création « À partir d'une photo » : fichier natif, aperçu local, photo réencodée dans le
   navigateur (JPEG, orientation appliquée, métadonnées EXIF/GPS retirées) puis envoyée seulement après la case de
   consentement ; jamais conservée dans le stockage local. Dépend d'atelier-core.js. Aucune bibliothèque. */

(() => {
  const A = window.OmbranceAtelier;
  if (!A || customElements.get('atelier-studio')) return;
  const { t, store, api, track } = A;

  const MIN = 3;
  const MAX = 300;
  const REFINE_MIN = 2;
  const POLL_SLOW = 45000;
  const POLL_LONG = 90000;
  const POLL_LIMIT = 300000;
  /* Étapes affichées pendant l'attente, selon le temps écoulé (ms) : le serveur peut relancer une génération après
     son contrôle qualité, l'attente dépasse alors la minute sans que rien ne soit perdu. */
  const STEPS = [[0, 'step_1'], [4000, 'step_2'], [9000, 'step_3'], [15000, 'step_4'], [40000, 'step_5'], [70000, 'step_6']];
  const RESULT_TTL = 24 * 3600 * 1000;
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const RATIO = { portrait: [3, 4], landscape: [4, 3] };
  /* Photo réencodée avant l'envoi : côté long plafonné (suffisant pour le modèle d'édition, envoi rapide sur mobile) */
  const PHOTO_MAX_SIDE = 3072;
  const PHOTO_RAW_LIMIT = 40 * 1048576;
  const variantNumber = (v) => { const m = String(v).match(/(\d+)\s*$/); return m ? Number(m[1]) : v; };
  const currency = () => A.config.currency || 'EUR';
  const clean = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  /* Texte au singulier ou au pluriel avec d'autres variables que {count} */
  const tn = (n, one, other, vars = {}) => t(Math.abs(n) < 2 ? one : other, { count: n, ...vars });

  class AtelierStudio extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.refs = {};
      this.querySelectorAll('[data-ref]').forEach((el) => { if (!this.refs[el.dataset.ref]) this.refs[el.dataset.ref] = el; });
      this.form = this.refs.form;
      this.creditsDialog = document.getElementById('AtelierCredits');
      this.styles = new Map();
      this.me = null;
      this.current = null;
      this.busy = false;
      this.source = 'text';
      this.photo = null;
      this.bind();
      this.init();
    }

    disconnectedCallback() {
      if (this.photo?.url) URL.revokeObjectURL(this.photo.url);
    }

    /* ---------- Initialisation ---------- */
    async init() {
      let me;
      try {
        me = A.normMe(await api.me());
      } catch (err) {
        this.showSoon();
        return;
      }
      if (!me.styles.length) { this.showSoon(); return; }
      this.me = me;
      this.renderStyles();
      this.setupPhoto();
      this.setFormEnabled(true);
      this.refs.wallet.hidden = false;
      this.dataset.state = 'ready';
      this.restoreDraft();
      this.applyStyleOptions();
      this.updateBalance();
      track('atelier_view', { logged_in: me.loggedIn, free_trial_available: me.freeTrialAvailable, balance: me.balance });
      this.applyParams();
      await this.resumeClaim();
      this.detectPurchase();
      const pending = store.get('pending');
      if (pending?.generationId) { this.fillForm(pending.meta); this.poll(pending); return; }
      const last = store.get('last');
      if (last?.artwork && Date.now() - (last.at || 0) < RESULT_TTL) {
        this.fillForm(last.meta);
        this.showResult(last, { restore: true });
      }
    }

    showSoon() {
      this.dataset.state = 'soon';
      this.refs.soon.hidden = false;
      this.querySelector('.studio__body').hidden = true;
    }

    renderStyles() {
      const box = this.refs.universes;
      const cards = new Map([...box.querySelectorAll('.u-card')].map((c) => [c.dataset.styleId, c]));
      this.me.styles.forEach((style) => {
        let card = cards.get(style.id);
        if (!card) card = this.createCard(style);
        const input = card.querySelector('input');
        input.value = style.id;
        input.disabled = false;
        if (style.name) card.querySelector('.u-card__name').textContent = style.name;
        if (style.description) card.querySelector('.u-card__desc').textContent = style.description;
        const media = card.querySelector('.u-card__media');
        if (!media.querySelector('img') && style.preview) {
          const img = new Image(480, 600);
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          img.src = A.sized(style.preview, 600);
          media.appendChild(img);
        }
        card.hidden = false;
        box.appendChild(card);
        this.styles.set(style.id, { ...style, name: card.querySelector('.u-card__name').textContent.trim() });
      });
      cards.forEach((card, id) => {
        if (this.styles.has(id)) return;
        card.hidden = true;
        card.querySelector('input').disabled = true;
      });
    }

    createCard(style) {
      const node = this.refs['universe-template'].content.firstElementChild.cloneNode(true);
      const id = `AtelierStyle-${this.dataset.section}-${style.id}`;
      node.dataset.styleId = style.id;
      node.htmlFor = id;
      node.querySelector('input').id = id;
      node.querySelector('.u-card__media').classList.add(`u-card__media--${style.id}`);
      return node;
    }

    styleName(id) { return this.styles.get(id)?.name || id || ''; }

    setFormEnabled(on) {
      this.form.querySelectorAll('input, textarea, button').forEach((el) => {
        if (el.name === 'style' && !this.styles.has(el.value)) return;
        if (el.name === 'style' && this.source === 'photo' && this.styles.get(el.value)?.photo === false) { el.disabled = true; return; }
        if (el.closest('.opt')?.hidden) return;
        el.disabled = !on;
      });
      this.form.setAttribute('aria-busy', on ? 'false' : 'true');
    }

    /* ---------- À partir d'une photo ---------- */
    setupPhoto() {
      const photo = this.me.photo;
      this.refs['source-field'].hidden = !photo;
      if (!photo) return;
      this.textCopy = {
        label: this.refs['description-label'].textContent,
        hint: this.refs['description-hint'].textContent,
        placeholder: this.refs.description.placeholder
      };
      /* Étapes renumérotées : 1 univers, 2 point de départ, 3 description ou précision, 4 options */
      this.refs['description-num'].textContent = '3';
      this.refs['options-num'].textContent = '4';
      this.refs['photo-hint'].textContent = t('photo_hint', { mb: Math.round(photo.maxBytes / 1048576), px: photo.minPx });
    }

    setSource(source, { focus = false } = {}) {
      const photoMode = source === 'photo' && Boolean(this.me?.photo);
      this.source = photoMode ? 'photo' : 'text';
      const radio = this.form.querySelector(`input[name="source"][value="${this.source}"]`);
      if (radio) radio.checked = true;
      this.refs['photo-panel'].hidden = !photoMode;
      const copy = this.textCopy || {};
      this.refs['description-label'].textContent = photoMode ? t('photo_detail_label') : copy.label || '';
      this.refs['description-hint'].textContent = photoMode ? t('photo_detail_hint') : copy.hint || '';
      this.refs.description.placeholder = photoMode ? t('photo_detail_placeholder') : copy.placeholder || '';
      this.refs.description.required = !photoMode;
      this.clearFieldError('description');
      /* Univers abstraits : non proposés pour une photo (les visages n'y resteraient pas reconnaissables) */
      this.styles.forEach((style, id) => {
        const input = this.refs.universes.querySelector(`input[value="${CSS.escape(id)}"]`);
        if (!input) return;
        const off = photoMode && style.photo === false;
        input.closest('.u-card').hidden = off;
        input.disabled = off || this.busy;
        if (off && input.checked) input.checked = false;
      });
      this.updateBalance();
      if (focus && photoMode) this.refs['photo-input'].focus();
    }

    /* Photo choisie : décodée et réencodée en JPEG dans le navigateur (orientation appliquée, aucune métadonnée), aperçu local */
    async selectPhoto(file) {
      this.clearPhotoError();
      this.discardPhoto();
      if (!file) return;
      const cfg = this.me.photo;
      const heic = /hei[cf]$/i.test(file.type) || /\.hei[cf]$/i.test(file.name || '');
      if (!/^image\//.test(file.type) && !heic) { this.setPhotoError(t('photo_type')); return; }
      if (file.size > PHOTO_RAW_LIMIT) { this.setPhotoError(t('photo_too_large', { mb: Math.round(cfg.maxBytes / 1048576) })); return; }
      this.refs['photo-meta'].textContent = t('photo_reading');
      let image;
      try {
        image = await this.decodePhoto(file);
      } catch (e) {
        this.refs['photo-meta'].textContent = '';
        this.setPhotoError(heic ? t('photo_heic') : t('photo_unreadable'));
        return;
      }
      const { width, height } = image;
      if (Math.min(width, height) < cfg.minPx) {
        image.close?.();
        this.setPhotoError(t('photo_too_small', { px: cfg.minPx }));
        return;
      }
      let blob;
      try {
        blob = await this.encodePhoto(image, width, height, cfg.maxBytes);
      } catch (e) {
        blob = /^image\/(jpeg|png|webp)$/.test(file.type) && file.size <= cfg.maxBytes ? file : null;
      } finally {
        image.close?.();
      }
      if (!blob) { this.setPhotoError(t('photo_too_large', { mb: Math.round(cfg.maxBytes / 1048576) })); return; }
      const url = URL.createObjectURL(blob);
      this.photo = { blob, url, width, height, photoId: null };
      const img = this.refs['photo-img'];
      const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(width, height));
      img.width = Math.round(width * scale);
      img.height = Math.round(height * scale);
      img.src = url;
      this.refs['photo-preview'].hidden = false;
      this.refs['photo-meta'].textContent = t('photo_selected', { width, height });
      const enhance = this.refs.enhance;
      this.refs['enhance-field'].hidden = !cfg.enhance;
      enhance.checked = cfg.enhance && Math.min(width, height) < cfg.enhanceBelowPx;
      A.announce(t('photo_ready'));
      track('photo_select', { small: Math.min(width, height) < cfg.enhanceBelowPx });
    }

    async decodePhoto(file) {
      if ('createImageBitmap' in window) {
        try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch (e) { /* repli : élément image */ }
      }
      const url = URL.createObjectURL(file);
      try {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        await img.decode();
        return { width: img.naturalWidth, height: img.naturalHeight, source: img, close() {} };
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 0);
      }
    }

    async encodePhoto(image, width, height, maxBytes) {
      const toBlob = (canvas, quality) => new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encodage'))), 'image/jpeg', quality);
      });
      for (const [side, quality] of [[PHOTO_MAX_SIDE, 0.9], [PHOTO_MAX_SIDE, 0.8], [2048, 0.82]]) {
        const scale = Math.min(1, side / Math.max(width, height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image.source || image, 0, 0, canvas.width, canvas.height);
        const blob = await toBlob(canvas, quality);
        if (blob.size <= maxBytes) return blob;
      }
      throw new Error('trop lourde');
    }

    /* Photo retirée ou remplacée : aperçu effacé ; déjà envoyée et pas encore utilisée → supprimée du serveur */
    discardPhoto() {
      const photo = this.photo;
      this.photo = null;
      if (!photo) return;
      if (photo.url) URL.revokeObjectURL(photo.url);
      if (photo.photoId) api.deletePhoto(photo.photoId).catch(() => {});
      this.refs['photo-preview'].hidden = true;
      this.refs['photo-img'].removeAttribute('src');
      this.refs['photo-meta'].textContent = '';
      this.refs['enhance-field'].hidden = true;
    }

    removePhoto() {
      this.discardPhoto();
      this.refs['photo-input'].value = '';
      this.clearPhotoError();
      this.refs['photo-input'].focus();
    }

    setPhotoError(message) {
      const box = this.refs['photo-error'];
      box.textContent = message;
      box.hidden = false;
      this.refs['photo-input'].setAttribute('aria-invalid', 'true');
      A.announce(message);
    }

    clearPhotoError() {
      this.refs['photo-error'].hidden = true;
      this.refs['photo-error'].textContent = '';
      this.refs['photo-input'].removeAttribute('aria-invalid');
    }

    setConsentError(message) {
      const box = this.refs['consent-error'];
      box.textContent = message;
      box.hidden = false;
      this.refs.consent.setAttribute('aria-invalid', 'true');
      A.announce(message);
    }

    clearConsentError() {
      this.refs['consent-error'].hidden = true;
      this.refs['consent-error'].textContent = '';
      this.refs.consent.removeAttribute('aria-invalid');
    }

    /* Envoi de la photo (une fois par création : le serveur la supprime à la fin du lot) → identifiant, ou null */
    async uploadPhoto() {
      if (this.photo.photoId) return this.photo.photoId;
      this.refs.cost.textContent = t('photo_uploading');
      try {
        const res = await api.uploadPhoto(this.photo.blob);
        this.photo.photoId = res.photoId;
        track('photo_upload', { enhance_suggested: Boolean(res.enhanceSuggested) });
        return res.photoId;
      } catch (err) {
        const code = err.code || 'ERROR';
        if (code === 'CONSENT_REQUIRED') this.setConsentError(t('photo_consent_required'));
        else if (code === 'UNSUPPORTED_TYPE') this.setPhotoError(err.data?.reason === 'HEIC' ? t('photo_heic') : t('photo_type'));
        else if (code === 'PHOTO_TOO_SMALL') this.setPhotoError(t('photo_too_small', { px: this.me.photo.minPx }));
        else if (code === 'PHOTO_TOO_LARGE' || code === 'PAYLOAD_TOO_LARGE' || code === 'PHOTO_DIMENSIONS' || err.status === 413) this.setPhotoError(t('photo_too_large', { mb: Math.round(this.me.photo.maxBytes / 1048576) }));
        else if (code === 'PHOTO_INVALID') this.setPhotoError(t('photo_unreadable'));
        else if (code === 'RATE_LIMITED') this.showAlert(t('error_rate_limited'));
        else if (code === 'PHOTO_DISABLED' || code === 'UNAVAILABLE') this.showAlert(t('photo_disabled'));
        else if (code === 'NETWORK' || code === 'OFFLINE') this.showAlert(t('error_network'));
        else this.showAlert(t('error_generic'));
        track('generation_failed', { reason: code.toLowerCase(), stage: 'photo_upload', source: 'photo' });
        return null;
      } finally {
        this.updateBalance();
      }
    }

    /* ---------- Événements ---------- */
    bind() {
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.refs.description.addEventListener('input', () => {
        this.updateCount();
        this.clearFieldError('description');
      });
      this.refs['proposals-list'].addEventListener('change', (e) => {
        if (e.target.name) this.selectProposal(Number(e.target.value));
      });
      this.refs['refine-form'].addEventListener('submit', (e) => { e.preventDefault(); this.submitRefine(); });
      this.refs['refine-input'].addEventListener('input', () => this.clearRefineError());
      this.form.addEventListener('change', (e) => {
        if (e.target.name === 'source') {
          this.setSource(e.target.value);
          track('atelier_source', { source: this.source });
        }
      });
      this.refs['photo-input'].addEventListener('change', (e) => { this.selectPhoto(e.target.files?.[0] || null); });
      this.refs.consent.addEventListener('change', () => this.clearConsentError());
      this.form.addEventListener('focusin', () => this.markStarted());
      this.refs.universes.addEventListener('change', () => { this.clearFieldError('style'); this.applyStyleOptions(); this.markStarted(); });
      this.addEventListener('click', (e) => this.onAction(e));
      this.creditsDialog?.addEventListener('click', (e) => {
        const button = e.target.closest('[data-pack]');
        if (button) this.buyPack(button.dataset.pack, button);
      });
      window.addEventListener('online', () => this.hideNotice());
    }

    markStarted() {
      if (this.hasStarted || !this.me) return;
      this.hasStarted = true;
      track('atelier_start', { logged_in: this.me.loggedIn, free_trial_available: this.me.freeTrialAvailable });
      this.prepareTurnstile();
    }

    onAction(e) {
      const el = e.target.closest('[data-action]');
      if (!el || !this.contains(el)) return;
      const action = el.dataset.action;
      const artwork = this.current?.artwork;
      switch (action) {
        case 'credits': this.openCredits('menu', el); break;
        case 'buy-first': {
          const pack = this.firstPack();
          if (pack) this.buyPack(pack.id, el);
          break;
        }
        case 'new': this.reset(); break;
        case 'photo-remove': this.removePhoto(); break;
        case 'variant': {
          /* Variante : nouvelle création à partir de la description d'origine (sans les précisions d'affinage) */
          if (!this.current) break;
          const { refinement, ...meta } = this.current.meta;
          this.generate(meta, { variantOf: artwork.id });
          break;
        }
        case 'download': if (artwork) this.download(artwork, el); break;
        case 'favorite': if (artwork) this.toggleFavorite(artwork, el); break;
        case 'print':
          if (!artwork) break;
          track('print_cta_click', { artwork_id: artwork.id, style_id: this.current.meta.styleId, source: 'studio' });
          document.dispatchEvent(new CustomEvent('atelier:print', { detail: { artwork, styleName: this.current.meta.styleName, trigger: el } }));
          break;
        case 'wall':
          if (artwork) document.dispatchEvent(new CustomEvent('atelier:wall', { detail: { artwork, trigger: el } }));
          break;
        case 'keep':
          if (artwork) store.set('claim', artwork.id);
          break;
        case 'check': {
          const pending = store.get('pending');
          if (pending) { pending.startedAt = Date.now() - POLL_SLOW; store.set('pending', pending); this.poll(pending); }
          break;
        }
        default:
      }
    }

    /* ---------- Formulaire ---------- */
    updateCount() {
      const n = this.refs.description.value.length;
      this.refs.count.textContent = t('counter', { count: n });
    }

    readForm() {
      const styleId = this.form.querySelector('input[name="style"]:checked')?.value || '';
      const description = this.refs.description.value.replace(/\s+/g, ' ').trim();
      const options = {};
      ['format', 'mood', 'light'].forEach((name) => {
        const value = this.form.querySelector(`input[name="${name}"]:checked`)?.value;
        if (value) options[name] = value;
      });
      return { styleId, description, options, source: this.source };
    }

    validate({ styleId, description, source }) {
      let first = null;
      const photoMode = source === 'photo';
      if (!styleId) {
        this.setFieldError('style', t(photoMode ? 'style_required_photo' : 'style_required'));
        first = first || this.refs.universes.querySelector('input:not([disabled])');
      }
      if (photoMode) {
        if (!this.photo) {
          this.setPhotoError(t('photo_required'));
          first = first || this.refs['photo-input'];
        }
        if (!this.refs.consent.checked) {
          this.setConsentError(t('photo_consent_required'));
          first = first || this.refs.consent;
        }
        if (description.length > MAX) {
          this.setFieldError('description', t('desc_long'));
          first = first || this.refs.description;
        }
      } else if (description.length < MIN || description.length > MAX) {
        this.setFieldError('description', description.length < MIN ? t('desc_short') : t('desc_long'));
        first = first || this.refs.description;
      }
      if (first) first.focus();
      return !first;
    }

    setFieldError(field, message, details = []) {
      const box = this.refs[`${field}-error`];
      box.textContent = message;
      details.forEach((detail) => {
        const line = document.createElement('span');
        line.className = 'field__detail';
        line.textContent = detail;
        box.appendChild(line);
      });
      box.hidden = false;
      if (field === 'description') this.refs.description.setAttribute('aria-invalid', 'true');
      else this.refs['universes-field'].setAttribute('aria-invalid', 'true');
      A.announce(message);
    }

    clearFieldError(field) {
      const box = this.refs[`${field}-error`];
      if (box.hidden) return;
      box.hidden = true;
      box.textContent = '';
      if (field === 'description') this.refs.description.removeAttribute('aria-invalid');
      else this.refs['universes-field'].removeAttribute('aria-invalid');
    }

    /* Options proposées par l'univers choisi (GET /me : styles[].options) ; une option masquée n'est jamais envoyée */
    applyStyleOptions() {
      const style = this.styles.get(this.form.querySelector('input[name="style"]:checked')?.value);
      ['format', 'mood', 'light'].forEach((group) => {
        const allowed = style?.options?.[group];
        const inputs = [...this.form.querySelectorAll(`input[name="${group}"]`)];
        inputs.forEach((input) => {
          const ok = !input.value || !Array.isArray(allowed) || allowed.includes(input.value);
          input.closest('.opt').hidden = !ok;
          input.disabled = !ok || this.busy;
        });
        const checked = inputs.find((i) => i.checked);
        if (!checked || checked.disabled) {
          const fallback = inputs.find((i) => !i.disabled && (i.value === (style?.defaults?.[group] || '') || !i.value)) || inputs.find((i) => !i.disabled);
          if (fallback) fallback.checked = true;
        }
      });
    }

    saveDraft() {
      const draft = this.readForm();
      if (draft.styleId || draft.description) store.set('draft', draft);
    }

    /* Reprise (actualisation, retour de connexion) : le formulaire retrouve la demande en cours ou affichée */
    fillForm(meta) {
      if (!meta || this.refs.description.value) return;
      this.restoreDraft({ ...meta, styleId: meta.styleId });
      this.applyStyleOptions();
    }

    /* ?style= présélectionne l'univers, ?idee= pré-remplit la description (formulaire GET de l'accueil, fiches produit) :
       ils priment sur le brouillon ; texte seulement (value), tronqué à la longueur maximale */
    restoreDraft(fromMeta) {
      const params = new URLSearchParams(location.search);
      const draft = fromMeta || store.get('draft');
      if (!fromMeta) store.del('draft');
      /* ?source=photo (lien « Créer à partir d'une photo ») ou reprise d'une création à partir d'une photo (la photo
         elle-même n'est jamais gardée : elle est à choisir de nouveau) */
      const source = (!fromMeta && params.get('source') === 'photo') ? 'photo' : draft?.source;
      if (source === 'photo' && this.me?.photo) this.setSource('photo');
      const styleId = params.get('style') || draft?.styleId;
      if (styleId && this.styles.has(styleId)) {
        const input = this.refs.universes.querySelector(`input[value="${CSS.escape(styleId)}"]`);
        if (input) input.checked = true;
      }
      const idea = fromMeta ? '' : clean(params.get('idee'));
      if (idea) track('atelier_prefill', { style: Boolean(params.get('style')), idea: true });
      const description = idea || draft?.description;
      if (description) this.refs.description.value = [...String(description)].slice(0, MAX).join('');
      Object.entries(draft?.options || {}).forEach(([name, value]) => {
        const input = this.form.querySelector(`input[name="${CSS.escape(name)}"][value="${CSS.escape(String(value))}"]`);
        if (input) input.checked = true;
      });
      this.updateCount();
    }

    applyParams() {
      const url = new URL(location.href);
      if (url.searchParams.get('credits') === '1') {
        const wanted = store.get('buy');
        store.del('buy');
        this.openCredits('menu');
        if (wanted) this.creditsDialog?.querySelector(`[data-pack="${CSS.escape(String(wanted))}"]`)?.focus();
      }
      let changed = false;
      ['style', 'idee', 'source', 'credits', 'atelier'].forEach((k) => { if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; } });
      if (changed) history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    }

    /* ---------- Solde et coût annoncé ---------- */
    balanceLabel() {
      const { me } = this;
      const credits = A.plural(me.balance, 'balance_one', 'balance_other');
      if (!me.freeTrialAvailable) return credits;
      const free = A.plural(Math.max(1, me.freeTrialsRemaining), 'balance_free_one', 'balance_free_other');
      return me.balance > 0 ? `${free} · ${credits}` : free;
    }

    /* « 4 propositions au choix. » (rien avec un serveur à une proposition) */
    proposalsNote(count = this.me.proposals) {
      return count > 1 ? ` ${t('proposals_note', { count })}` : '';
    }

    updateBalance() {
      const { me } = this;
      this.refs.balance.textContent = this.balanceLabel();
      const free = me.freeTrialAvailable;
      if (this.source === 'photo' && me.photo) this.updatePhotoCost();
      else {
        if (!this.busy) this.refs.submit.textContent = free ? t('generate_free') : t('generate_credit');
        let cost = `${t('cost_credit')}${this.proposalsNote()}`;
        if (free) cost = `${tn(Math.max(1, me.freeTrialsRemaining), 'cost_free_one', 'cost_free_other')}${this.proposalsNote()}`;
        else if (me.balance < 1) {
          if (me.loggedIn) cost = t('cost_empty');
          else cost = !me.freeTrialReason || me.freeTrialReason === 'FREE_TRIAL_USED' ? t('cost_login') : t('cost_login_other');
        }
        this.refs.cost.textContent = cost;
      }
      this.refs.variant.textContent = free ? t('variant') : t('variant_cost');
      if (this.current) this.updateRefine();
    }

    /* Création à partir d'une photo : PHOTO_CREDITS crédits (ou autant de créations offertes), annoncés avant l'envoi */
    updatePhotoCost() {
      const { me } = this;
      const count = me.photo.credits;
      const free = me.photo.freeAvailable;
      if (!this.busy) this.refs.submit.textContent = free ? t('generate_free') : t('generate_photo', { count });
      let cost;
      if (free) cost = `${t('cost_photo_free', { count })}${this.proposalsNote(me.photo.proposals)}`;
      else if (me.balance >= count) cost = `${t('cost_photo_credit', { count })}${this.proposalsNote(me.photo.proposals)}`;
      else if (me.loggedIn) cost = t('cost_photo_empty', { count, balance: me.balance });
      else cost = t('cost_photo_login', { count });
      this.refs.cost.textContent = cost;
    }

    /* Crédits demandés par une création ou un affinage payant (photo : PHOTO_CREDITS) */
    creditsFor(source) {
      return source === 'photo' && this.me.photo ? this.me.photo.credits : 1;
    }

    async refreshMe() {
      try {
        this.me = A.normMe(await api.me());
        this.updateBalance();
      } catch (e) { /* le solde affiché reste le dernier connu */ }
      return this.me;
    }

    /* ---------- Génération ---------- */
    submit() {
      if (this.busy || !this.me) return;
      const request = this.readForm();
      if (!this.validate(request)) return;
      this.generate(request);
    }

    /* extra : { variantOf } (variante : nouvelle création, même description) ou { refineOf, refinement } (affinage) */
    async generate(request, extra = {}) {
      if (this.busy || this.starting) return;
      this.starting = true;
      try {
        await this.prepareAndSend(request, extra);
      } finally {
        this.starting = false;
      }
    }

    async prepareAndSend(request, extra) {
      this.clearAlert();
      const refine = Boolean(extra.refineOf);
      const photoMode = !refine && request.source === 'photo' && Boolean(this.me.photo);
      const source = refine ? (this.current?.meta?.source || 'text') : photoMode ? 'photo' : 'text';
      const needed = this.creditsFor(source);
      const isFree = () => {
        if (refine) return this.refineFreeLeft() > 0;
        return photoMode ? Boolean(this.me.photo?.freeAvailable) : this.me.freeTrialAvailable;
      };
      if (!isFree() && this.me.balance < needed) await this.refreshMe();
      if (!isFree() && (!this.me.loggedIn || this.me.balance < needed)) {
        if (!refine) this.saveDraft();
        if (this.me.loggedIn) this.openCredits('no_credits');
        else this.showLoginRequired();
        return;
      }
      /* Photo : envoyée maintenant (consentement coché), une fois ; elle n'est jamais gardée côté navigateur au-delà de la page */
      let photoId = null;
      if (photoMode) {
        photoId = await this.uploadPhoto();
        if (!photoId) return;
      }
      /* Coût annoncé (bouton et ligne de coût) transmis : le serveur refuse de débiter une demande annoncée offerte */
      const cost = isFree() ? 'free' : 'credit';
      let turnstileToken;
      if (!this.me.loggedIn && A.config.turnstileKey) {
        this.refs.cost.textContent = t('turnstile_wait');
        try {
          turnstileToken = await this.turnstileToken();
        } catch (e) {
          this.updateBalance();
          this.showAlert(t('error_turnstile'));
          return;
        }
        this.updateBalance();
      }
      const meta = {
        styleId: request.styleId,
        styleName: this.styleName(request.styleId),
        description: request.description,
        options: request.options || {},
        source,
        ...(request.refinement ? { refinement: request.refinement } : {})
      };
      const payload = refine
        ? { refineOf: extra.refineOf, refinement: extra.refinement, styleId: meta.styleId, cost }
        : { styleId: meta.styleId, description: meta.description, options: meta.options, cost };
      if (photoMode) Object.assign(payload, { source: 'photo', photoId, enhance: Boolean(this.me.photo.enhance && this.refs.enhance.checked) });
      if (extra.variantOf) payload.variantOf = extra.variantOf;
      if (turnstileToken) payload.turnstileToken = turnstileToken;
      this.send({ key: A.uuid(), payload, meta, kind: refine ? 'refine' : 'creation', source, credits: needed });
    }

    async send(attempt) {
      this.clearAlert();
      this.clearRefineError();
      this.setBusy(true);
      this.startWaiting(Date.now());
      let res;
      try {
        res = await api.generate(attempt.payload, attempt.key);
      } catch (err) {
        this.setBusy(false);
        this.stopWaiting();
        this.restoreStage();
        this.handleStartError(err, attempt);
        return;
      } finally {
        this.resetTurnstile();
      }
      const charged = res.charged === 'free' ? 'free' : 'credit';
      const kind = res.kind === 'refine' || attempt.kind === 'refine' ? 'refine' : 'creation';
      const source = res.source === 'photo' || attempt.source === 'photo' ? 'photo' : 'text';
      if (charged === 'free' && kind === 'creation') {
        /* Une création à partir d'une photo compte pour PHOTO_CREDITS créations offertes */
        this.me.freeTrialsRemaining = Math.max(0, this.me.freeTrialsRemaining - (source === 'photo' ? attempt.credits || 1 : 1));
        this.me.freeTrialAvailable = this.me.freeTrialsRemaining > 0;
      }
      if (this.me.photo) this.me.photo.freeAvailable = this.me.photo.freeAvailable && this.me.freeTrialsRemaining >= this.me.photo.credits;
      /* La photo a servi : le serveur la supprime à la fin du lot ; une nouvelle création la renverra */
      if (source === 'photo' && kind === 'creation' && this.photo) this.photo.photoId = null;
      if (typeof res.balance === 'number') this.me.balance = res.balance;
      this.updateBalance();
      const data = { style_id: attempt.meta.styleId, charged, generation_id: res.generationId, variant: Boolean(attempt.payload.variantOf), kind, source, proposals: res.proposals || 1 };
      track('generation_start', data);
      if (charged === 'free' && kind === 'creation') track('free_generation_start', data);
      if (kind === 'refine') track('refine_start', data);
      const pending = { generationId: res.generationId, charged, kind, meta: attempt.meta, startedAt: Date.now() };
      store.set('pending', pending);
      this.poll(pending);
    }

    handleStartError(err, attempt) {
      const code = err.code || 'ERROR';
      if (attempt.kind === 'refine' && this.handleRefineError(err)) {
        track('generation_failed', { reason: code.toLowerCase(), stage: 'start', style_id: attempt.meta.styleId, kind: 'refine' });
        return;
      }
      const photoStart = attempt.source === 'photo' && attempt.kind !== 'refine';
      /* Refus propres à la photo (création offerte réservée, plafond du jour) : les créations « texte » offertes restent */
      const photoOnly = photoStart && ['PHOTO_FREE_INSUFFICIENT', 'PHOTO_FREE_LIMIT'].includes(err.data?.reason);
      switch (code) {
        case 'COST_CHANGED':
          if (photoStart && this.me.photo) this.me.photo.freeAvailable = false;
          else {
            this.me.freeTrialAvailable = false;
            this.me.freeTrialsRemaining = 0;
          }
          this.updateBalance();
          this.showAlert(photoStart ? t('cost_changed_photo', { count: this.creditsFor('photo') }) : t('cost_changed'));
          this.refreshMe();
          break;
        case 'LOGIN_REQUIRED':
          if (attempt.kind === 'refine') {
            if (this.current) this.current.refine = { freeLeft: 0 };
          } else if (photoOnly) {
            if (this.me.photo) this.me.photo.freeAvailable = false;
            this.saveDraft();
          } else {
            this.me.freeTrialAvailable = false;
            this.me.freeTrialsRemaining = 0;
            if (this.me.photo) this.me.photo.freeAvailable = false;
            this.me.freeTrialReason = err.data?.reason || this.me.freeTrialReason;
            this.saveDraft();
          }
          this.updateBalance();
          this.showLoginRequired();
          break;
        case 'NO_CREDITS':
          if (attempt.kind !== 'refine') {
            if (!photoStart) {
              this.me.freeTrialAvailable = false;
              this.me.freeTrialsRemaining = 0;
            }
            if (this.me.photo) this.me.photo.freeAvailable = false;
            this.saveDraft();
          }
          this.me.balance = typeof err.data?.balance === 'number' ? err.data.balance : 0;
          this.updateBalance();
          this.showAlert(t('error_no_credits'));
          this.openCredits('no_credits');
          break;
        case 'BLOCKED': {
          const reasons = Array.isArray(err.data?.reasons) ? err.data.reasons.map((r) => r && r.message).filter(Boolean) : [];
          this.setFieldError('description', t('error_blocked'), [...new Set(reasons)].slice(0, 3));
          this.refs.description.focus();
          break;
        }
        case 'PHOTO_EXPIRED':
        case 'PHOTO_NOT_FOUND':
          /* Photo déjà utilisée, expirée ou supprimée : elle sera renvoyée au prochain essai */
          if (this.photo) this.photo.photoId = null;
          this.setPhotoError(t('photo_expired'));
          break;
        case 'PHOTO_DISABLED':
          this.showAlert(t('photo_disabled'));
          break;
        case 'CAPTCHA_FAILED':
          this.showAlert(t('error_turnstile'));
          break;
        case 'INVALID_INPUT':
          if (err.data?.field === 'description') this.setFieldError('description', t('desc_short'));
          else if (err.data?.reason === 'STYLE_NOT_FOR_PHOTO') this.setFieldError('style', t('style_required_photo'));
          else if (err.data?.field === 'photo') this.setPhotoError(t('photo_required'));
          else this.showAlert(t('error_generic'));
          break;
        case 'RATE_LIMITED':
          this.showAlert(t('error_rate_limited'));
          this.cooldown(Math.min(err.retryAfter || 20, 120));
          break;
        case 'PROVIDER_DOWN':
          this.showAlert(t('error_provider_down'));
          break;
        case 'NETWORK':
        case 'OFFLINE':
          this.showAlert(t('error_network'), () => this.send(attempt));
          break;
        case 'UNAVAILABLE':
          this.showSoon();
          break;
        default:
          this.showAlert(t('error_generic'));
      }
      track('generation_failed', { reason: code.toLowerCase(), stage: 'start', style_id: attempt.meta.styleId, variant: Boolean(attempt.payload.variantOf), kind: attempt.kind || 'creation', source: attempt.source || 'text' });
    }

    async poll(pending) {
      const token = {};
      this.pollToken = token;
      this.progress = '';
      this.setBusy(true);
      this.startWaiting(pending.startedAt);
      let delay = 1200;
      let failures = 0;
      while (this.pollToken === token) {
        await A.sleep(delay);
        if (this.pollToken !== token) return;
        delay = Math.min(Math.round(delay * 1.35), 5000);
        const elapsed = Date.now() - pending.startedAt;
        if (elapsed > POLL_LIMIT) { this.pollTimeout(); return; }
        this.refs['waiting-note'].textContent = this.waitingNote(elapsed);
        if (navigator.onLine === false) { this.showNotice(t('error_network')); continue; }
        let res;
        try {
          res = await api.generation(pending.generationId);
          failures = 0;
          this.hideNotice();
        } catch (err) {
          if (err.status === 404) { this.finishFailure(pending, { status: 'failed' }); return; }
          failures += 1;
          if (err.code === 'NETWORK' || err.code === 'OFFLINE' || failures > 2) this.showNotice(t('error_network'));
          continue;
        }
        if (typeof res.balance === 'number' && this.me) { this.me.balance = res.balance; this.updateBalance(); }
        const p = res.proposals;
        this.progress = p && p.total > 1 && p.ready > 0 ? t('waiting_progress', { ready: p.ready, total: p.total }) : '';
        if (res.status === 'succeeded') {
          /* artworks[] (propositions) ; artwork seul : serveur à une proposition */
          const list = Array.isArray(res.artworks) && res.artworks.length ? res.artworks : [res.artwork];
          const artworks = list.map(A.normArtwork).filter(Boolean);
          if (artworks.length) { await this.finishSuccess(pending, artworks, res); return; }
        }
        if (res.status === 'failed' || res.status === 'blocked') { this.finishFailure(pending, res); return; }
      }
    }

    pollTimeout() {
      this.pollToken = null;
      this.stopWaiting(true);
      this.refs['waiting-note'].textContent = t('waiting_timeout');
      this.querySelector('[data-action="check"]').hidden = false;
      this.setBusy(false);
    }

    async finishSuccess(pending, artworks, res = {}) {
      this.pollToken = null;
      this.progress = '';
      store.del('pending');
      const meta = { ...pending.meta, styleName: pending.meta.styleName || this.styleName(pending.meta.styleId) };
      const kind = res.kind === 'refine' || pending.kind === 'refine' ? 'refine' : 'creation';
      const total = res.proposals?.total || artworks.length;
      const entry = {
        artworks, selected: 0, artwork: artworks[0], meta, charged: pending.charged, kind,
        refine: res.refine && typeof res.refine.freeLeft === 'number' ? { freeLeft: res.refine.freeLeft } : null,
        credits: typeof res.credits === 'number' ? res.credits : pending.charged === 'credit' ? 1 : 0,
        missing: Math.max(0, total - artworks.length), at: Date.now()
      };
      if (res.source === 'photo') meta.source = 'photo';
      store.set('last', entry);
      const data = { style_id: meta.styleId, charged: pending.charged, generation_id: pending.generationId, artwork_id: artworks[0].id, kind, proposals: artworks.length };
      track('generation_complete', data);
      if (pending.charged === 'free' && kind === 'creation') track('free_generation_complete', data);
      if (kind === 'refine') {
        this.refs['refine-input'].value = '';
        this.refs['refine-box'].open = false;
      }
      await this.showResult(entry);
    }

    finishFailure(pending, res) {
      this.pollToken = null;
      this.progress = '';
      store.del('pending');
      this.stopWaiting();
      this.setBusy(false);
      this.restoreStage();
      const blocked = res.status === 'blocked';
      /* Photo refusée par le contrôle de contenu du fournisseur : tout le lot est refusé (crédits rendus) */
      const photoBlocked = res.reason === 'PHOTO_BLOCKED';
      let message = t('error_failed');
      if (res.refunded) {
        if (pending.charged === 'free') message = t('error_free_released');
        else message = (res.credits || 1) > 1 ? t('error_refunded_many', { count: res.credits }) : t('error_refunded');
      }
      if (photoBlocked) message = pending.charged === 'free' ? t('error_photo_blocked') : `${t('error_photo_blocked')} ${(res.credits || 1) > 1 ? t('credits_returned_many', { count: res.credits }) : t('credits_returned_one')}`;
      if (blocked) this.setFieldError('description', t('error_blocked'));
      else if (pending.kind === 'refine' && this.current) this.setRefineError(message);
      else if (photoBlocked && this.source === 'photo') this.setPhotoError(message);
      else this.showAlert(message);
      track('generation_failed', { reason: blocked ? 'blocked' : photoBlocked ? 'photo_blocked' : 'failed', stage: 'generation', refunded: Boolean(res.refunded), style_id: pending.meta?.styleId, generation_id: pending.generationId, source: pending.meta?.source || 'text' });
      this.refreshMe();
    }

    setBusy(on) {
      this.busy = on;
      this.dataset.state = on ? 'busy' : 'ready';
      this.setFormEnabled(!on);
      this.querySelectorAll('.stage__actions button, .stage__refine-form input, .stage__refine-form button').forEach((b) => { b.disabled = on; });
      if (!on && this.me) this.updateBalance();
    }

    cooldown(seconds) {
      const button = this.refs.submit;
      button.disabled = true;
      clearTimeout(this.cooldownTimer);
      this.cooldownTimer = setTimeout(() => { if (!this.busy) button.disabled = false; }, seconds * 1000);
    }

    /* ---------- Scène : attente et résultat ---------- */
    setStage(name) {
      const { stage, idle, waiting, image, result } = this.refs;
      stage.dataset.stage = name;
      idle.hidden = name !== 'idle';
      waiting.hidden = name !== 'waiting';
      image.hidden = name !== 'result';
      result.hidden = name !== 'result';
    }

    restoreStage() { this.setStage(this.current ? 'result' : 'idle'); }

    startWaiting(startedAt) {
      this.stopWaiting();
      this.setStage('waiting');
      this.querySelector('[data-action="check"]').hidden = true;
      const tick = () => {
        const elapsed = Date.now() - startedAt;
        const step = STEPS.filter(([at]) => elapsed >= at).pop()[1];
        if (this.refs.step.textContent !== t(step)) this.refs.step.textContent = t(step);
        this.refs['waiting-note'].textContent = this.waitingNote(elapsed);
      };
      tick();
      this.stepTimer = setInterval(tick, 1000);
      this.refs.stage.classList.add('is-waiting');
      A.announce(t('waiting_title'));
      this.reveal();
    }

    waitingNote(elapsed) {
      if (this.progress) return this.progress;
      if (elapsed > POLL_LONG) return t('waiting_long');
      return elapsed > POLL_SLOW ? t('waiting_slow') : t('waiting_note');
    }

    stopWaiting(keepVisible = false) {
      clearInterval(this.stepTimer);
      if (!keepVisible) this.refs.stage.classList.remove('is-waiting');
    }

    reveal() {
      if (matchMedia('(min-width: 990px)').matches) return;
      const rect = this.refs.stage.getBoundingClientRect();
      if (rect.top < 0 || rect.top > innerHeight * 0.6) this.refs.stage.scrollIntoView({ behavior: A.reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    async showResult(entry, { restore = false } = {}) {
      this.stopWaiting();
      this.setBusy(false);
      /* Résultat enregistré par une version à une seule œuvre */
      if (!Array.isArray(entry.artworks) || !entry.artworks.length) entry.artworks = [entry.artwork];
      if (!entry.artworks[entry.selected]) entry.selected = 0;
      entry.artwork = entry.artworks[entry.selected];
      this.current = entry;
      const { artwork, meta } = entry;
      meta.styleName = meta.styleName || this.styleName(meta.styleId) || artwork.styleName;
      this.paintArtwork(entry);
      try { await this.refs.image.decode(); } catch (e) { /* affichée au chargement */ }
      this.renderProposals(entry);

      this.refs['result-style'].textContent = meta.styleName;
      this.refs.usage.textContent = this.usageLabel(entry);
      this.setFavorite(artwork.favorite);
      this.updateRefine();
      /* Variante impossible à partir d'une photo (la photo est déjà supprimée) : l'affinage la remplace */
      this.refs.variant.hidden = meta.source === 'photo';

      const pack = this.firstPack();
      const trialResult = entry.charged === 'free' && entry.kind !== 'refine';
      this.refs.first.hidden = !trialResult;
      if (trialResult) {
        const remaining = this.me?.freeTrialAvailable ? this.me.freeTrialsRemaining : 0;
        const total = this.me?.freeTrialsTotal || 1;
        this.refs['first-title'].textContent = remaining >= total - 1 ? t('first_title') : t('free_title');
        this.refs['first-text'].textContent = remaining > 0 ? A.plural(remaining, 'free_left_text_one', 'free_left_text_other') : t('first_text');
      }
      this.refs['first-cta'].hidden = !pack;
      if (pack) this.refs['first-cta'].textContent = t('first_cta', { count: pack.credits, price: A.money(pack.price) });

      const anonymous = !this.me?.loggedIn;
      this.refs.keep.hidden = !(anonymous && entry.charged === 'free');
      const keepUrl = A.loginUrl(`${A.config.atelierUrl || '/pages/atelier'}?atelier=keep`);
      this.refs['keep-link'].href = keepUrl;
      this.refs['keep-login'].href = keepUrl;

      this.setStage('result');
      if (restore) return;
      const img = this.refs.image;
      if (!A.reduceMotion()) {
        img.classList.remove('is-developing');
        void img.offsetWidth;
        img.classList.add('is-developing');
      }
      A.announce(entry.artworks.length > 1 ? t('ready_many', { count: entry.artworks.length }) : t('ready'));
      this.refs['result-heading'].focus({ preventScroll: true });
      this.reveal();
    }

    usageLabel(entry) {
      let usage;
      const credits = entry.credits || 1;
      if (entry.charged === 'free' && entry.kind === 'refine') usage = t('used_refine_free');
      else if (entry.charged === 'free') {
        usage = entry.meta?.source === 'photo' && this.me?.photo ? t('used_free_photo', { count: this.me.photo.credits }) : t('used_free');
        if (this.me?.freeTrialAvailable && this.me.freeTrialsRemaining > 0) usage += ` · ${A.plural(this.me.freeTrialsRemaining, 'free_left_one', 'free_left_other')}`;
      } else usage = `${credits > 1 ? t('used_credits', { count: credits }) : t('used_credit')} · ${A.plural(this.me?.balance || 0, 'left_one', 'left_other')}`;
      if (entry.missing > 0) usage += ` · ${t('proposals_missing', { count: entry.missing })}`;
      return usage;
    }

    /* Grande image : la proposition choisie */
    paintArtwork(entry) {
      const { artwork, meta } = entry;
      const img = this.refs.image;
      const [rw, rh] = RATIO[artwork.aspect] || RATIO.portrait;
      img.width = artwork.width || rw * 400;
      img.height = artwork.height || rh * 400;
      img.sizes = '(min-width: 990px) 38rem, 100vw';
      img.srcset = A.srcset(artwork.previewUrl, [480, 720, 960, 1280, 1600]);
      img.src = A.sized(artwork.previewUrl, 960);
      const description = [meta.description || artwork.description || '', meta.refinement].filter(Boolean).join(' — ');
      const total = entry.artworks.length;
      img.alt = t('result_alt', { style: meta.styleName, description }) + (total > 1 ? ` (${t('proposal_label', { index: entry.selected + 1, total })})` : '');
    }

    /* Propositions : groupe de boutons radio natifs (flèches du clavier, nom accessible « Proposition 2 sur 4 ») */
    renderProposals(entry) {
      const list = this.refs['proposals-list'];
      const total = entry.artworks.length;
      list.replaceChildren();
      this.refs.proposals.hidden = total < 2;
      if (total < 2) return;
      const name = `AtelierProposal-${this.dataset.section}`;
      entry.artworks.forEach((art, i) => {
        const label = document.createElement('label');
        label.className = 'proposal';
        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'proposal__input';
        input.name = name;
        input.value = String(i);
        input.checked = i === entry.selected;
        const media = document.createElement('span');
        media.className = 'proposal__media';
        const [rw, rh] = RATIO[art.aspect] || RATIO.portrait;
        media.style.setProperty('--ratio', `${rw} / ${rh}`);
        const img = new Image(rw * 60, rh * 60);
        img.alt = '';
        img.decoding = 'async';
        img.sizes = '(min-width: 990px) 9rem, 24vw';
        img.srcset = A.srcset(art.previewUrl, [160, 240, 360]);
        img.src = A.sized(art.previewUrl, 240);
        media.appendChild(img);
        const text = document.createElement('span');
        text.className = 'visually-hidden';
        text.textContent = t('proposal_label', { index: i + 1, total });
        label.append(input, media, text);
        list.appendChild(label);
      });
    }

    selectProposal(index) {
      const entry = this.current;
      if (!entry || !entry.artworks[index] || index === entry.selected) return;
      entry.selected = index;
      entry.artwork = entry.artworks[index];
      store.set('last', entry);
      this.paintArtwork(entry);
      this.setFavorite(entry.artwork.favorite);
      this.clearRefineError();
      track('proposal_select', { artwork_id: entry.artwork.id, index, total: entry.artworks.length, style_id: entry.meta.styleId });
    }

    /* ---------- Affinage : précision courte sur la proposition choisie, coût annoncé avant l'envoi ---------- */
    refineFreeLeft() {
      const cfg = this.me?.refine;
      if (!cfg) return 0;
      /* Inconnu (résultat restauré) : la règle générale ; le serveur refuse de débiter une demande annoncée offerte */
      return this.current?.refine ? this.current.refine.freeLeft : cfg.freePerCreation;
    }

    updateRefine() {
      const cfg = this.me?.refine;
      const box = this.refs['refine-box'];
      box.hidden = !cfg || !this.current;
      if (box.hidden) return;
      const freeLeft = this.refineFreeLeft();
      /* Affinage payant d'une création à partir d'une photo : le prix d'une création photo */
      const count = this.creditsFor(this.current.meta?.source);
      let cost;
      if (freeLeft > 0) cost = tn(freeLeft, 'refine_cost_free_one', 'refine_cost_free_other', { proposals: cfg.freeProposals });
      else if (!this.me.loggedIn) cost = t('refine_cost_login', { proposals: cfg.paidProposals });
      else cost = count > 1 ? t('refine_cost_credits', { count, proposals: cfg.paidProposals }) : t('refine_cost_credit', { proposals: cfg.paidProposals });
      this.refs['refine-cost'].textContent = cost;
      let label = count > 1 ? t('refine_button_credits', { count }) : t('refine_button_credit');
      if (freeLeft > 0) label = t('refine_button_free');
      else if (!this.me.loggedIn) label = t('refine_button_login');
      if (!this.busy) this.refs['refine-submit'].textContent = label;
      this.refs['refine-input'].maxLength = cfg.max;
    }

    submitRefine() {
      if (this.busy || !this.current || !this.me?.refine) return;
      const { meta, artwork } = this.current;
      /* Visiteur sans affinage offert : connexion (la création est rattachée au retour, comme « Conserver ») */
      if (this.refineFreeLeft() < 1 && !this.me.loggedIn) {
        store.set('claim', artwork.id);
        location.href = A.loginUrl(`${A.config.atelierUrl || '/pages/atelier'}?atelier=keep`);
        return;
      }
      const input = this.refs['refine-input'];
      const text = clean(input.value);
      if (text.length < REFINE_MIN || text.length > this.me.refine.max) {
        this.setRefineError(text.length < REFINE_MIN ? t('refine_short') : t('refine_long'));
        input.focus();
        return;
      }
      const refinement = meta.refinement ? `${meta.refinement} ; ${text}` : text;
      this.generate({ ...meta, refinement }, { refineOf: artwork.id, refinement: text });
    }

    /* Erreurs propres à l'affinage (affichées sous la précision) ; false : traitement général */
    handleRefineError(err) {
      switch (err.code) {
        case 'BLOCKED': {
          const reasons = Array.isArray(err.data?.reasons) ? err.data.reasons.map((r) => r && r.message).filter(Boolean) : [];
          this.setRefineError(t('refine_blocked'), [...new Set(reasons)].slice(0, 3));
          return true;
        }
        case 'INVALID_INPUT':
          if (err.data?.field !== 'refinement') return false;
          this.setRefineError(t('refine_short'));
          return true;
        case 'COST_CHANGED':
          if (this.current) this.current.refine = { freeLeft: 0 };
          if (this.current) store.set('last', this.current);
          this.updateRefine();
          this.setRefineError(t('cost_changed'));
          return true;
        default:
          return false;
      }
    }

    setRefineError(message, details = []) {
      const box = this.refs['refine-error'];
      box.textContent = message;
      details.forEach((detail) => {
        const line = document.createElement('span');
        line.className = 'field__detail';
        line.textContent = detail;
        box.appendChild(line);
      });
      box.hidden = false;
      this.refs['refine-box'].open = true;
      this.refs['refine-input'].setAttribute('aria-invalid', 'true');
      this.refs['refine-input'].focus({ preventScroll: true });
      A.announce(message);
    }

    clearRefineError() {
      const box = this.refs['refine-error'];
      if (box.hidden) return;
      box.hidden = true;
      box.textContent = '';
      this.refs['refine-input'].removeAttribute('aria-invalid');
    }

    reset() {
      this.current = null;
      store.del('last');
      this.clearAlert();
      this.clearRefineError();
      this.refs['refine-input'].value = '';
      this.refs['refine-box'].open = false;
      this.setStage('idle');
      this.refs.description.focus({ preventScroll: true });
      this.form.scrollIntoView({ behavior: A.reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    setFavorite(on) {
      const button = this.refs.favorite;
      button.setAttribute('aria-pressed', String(Boolean(on)));
      this.refs['favorite-label'].textContent = on ? t('favorite_remove') : t('favorite_add');
    }

    async toggleFavorite(artwork, button) {
      const next = button.getAttribute('aria-pressed') !== 'true';
      this.setFavorite(next);
      try {
        await api.favorite(artwork.id, next);
        artwork.favorite = next;
        if (this.current) store.set('last', this.current);
        track('artwork_favorite', { artwork_id: artwork.id, favorite: next });
      } catch (err) {
        this.setFavorite(!next);
        this.explainAccountError(err);
      }
    }

    async download(artwork, button) {
      button.disabled = true;
      try {
        const res = await api.download(artwork.id);
        const url = res.url || res.downloadUrl;
        if (!url) throw new A.ApiError('ERROR');
        const link = document.createElement('a');
        link.href = url;
        link.rel = 'noopener';
        link.download = '';
        document.body.appendChild(link);
        link.click();
        link.remove();
        A.announce(t('download_started'));
        track('artwork_download', { artwork_id: artwork.id });
      } catch (err) {
        this.explainAccountError(err);
      } finally {
        button.disabled = false;
      }
    }

    explainAccountError(err) {
      if (err.status === 401) {
        this.refs.keep.hidden = false;
        this.showAlert(t('keep_required'));
      } else if (err.code === 'NETWORK' || err.code === 'OFFLINE') {
        this.showAlert(t('error_network'));
      } else {
        this.showAlert(t('error_generic'));
      }
    }

    /* ---------- Compte : connexion, rattachement de la création offerte ---------- */
    showLoginRequired() {
      const box = this.refs.alert;
      box.replaceChildren();
      const title = document.createElement('p');
      title.className = 'studio__alert-title';
      title.textContent = t('login_title');
      const text = document.createElement('p');
      text.textContent = t('login_text');
      const link = document.createElement('a');
      link.className = 'button button--small';
      link.href = A.loginUrl(A.config.atelierUrl || '/pages/atelier');
      link.textContent = A.config.loginLabel || '';
      box.append(title, text, link);
      box.hidden = false;
    }

    /* Retour connecté : rattachement de la création offerte choisie avant la connexion et des créations de cet appareil
       encore sans compte (GET /me : claimable, contrat § 4) */
    async resumeClaim() {
      if (!this.me.loggedIn) return;
      const ids = [...new Set([store.get('claim'), ...this.me.claimable].filter(Boolean))];
      if (!ids.length) return;
      let claimed = 0;
      let conflict = false;
      let retry = false;
      for (const id of ids) {
        try {
          await api.claim(id);
          claimed += 1;
        } catch (err) {
          if (err.status === 409) conflict = true;
          else if (err.status !== 404) retry = true;
        }
      }
      if (!retry) store.del('claim');
      if (claimed) {
        track('signup_after_generation', { artwork_id: ids[0], count: claimed });
        this.showNotice(t('claimed'), true);
      } else if (conflict) {
        this.showNotice(t('claim_conflict'));
      }
    }

    /* ---------- Crédits ---------- */
    firstPack() {
      const packs = this.me?.packs || [];
      return packs.find((p) => p.credits === 5) || [...packs].sort((a, b) => a.price - b.price)[0] || null;
    }

    openCredits(reason, trigger) {
      const dialog = this.creditsDialog;
      if (!dialog || !this.me) return;
      const q = (ref) => dialog.querySelector(`[data-ref="${ref}"]`);
      q('credits-reason').hidden = reason !== 'no_credits';
      q('credits-balance').textContent = t('credits_balance', { balance: this.balanceLabel() });
      q('credits-login').hidden = this.me.loggedIn;
      q('credits-status').textContent = '';
      const list = q('packs');
      list.replaceChildren();
      const packs = this.me.packs.filter((p) => p.variantId);
      packs.forEach((pack) => {
        const li = document.createElement('li');
        li.className = `pack${pack.bestValue ? ' pack--popular' : ''}`;
        const head = document.createElement('div');
        head.className = 'pack__head';
        const credits = document.createElement('p');
        credits.className = 'pack__credits';
        credits.textContent = t('credits_pack', { count: pack.credits });
        head.appendChild(credits);
        // Badge factuel calculé par le serveur (prix par crédit le plus bas), jamais une popularité supposée.
        if (pack.bestValue) {
          const badge = document.createElement('span');
          badge.className = 'pack__badge';
          badge.textContent = t('credits_best_value');
          head.appendChild(badge);
        }
        const price = document.createElement('p');
        price.className = 'pack__price';
        price.textContent = A.money(pack.price);
        const unit = document.createElement('p');
        unit.className = 'pack__unit text-xs';
        unit.textContent = t('credits_unit', { price: A.money(Math.round(pack.price / pack.credits)) });
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `button button--small${pack.bestValue ? '' : ' button--secondary'}`;
        button.dataset.pack = pack.id;
        button.textContent = this.me.loggedIn ? t('credits_choose') : t('credits_login_choose');
        button.setAttribute('aria-label', `${button.textContent} : ${credits.textContent}, ${price.textContent}`);
        li.append(head, price, unit, button);
        list.appendChild(li);
      });
      if (!packs.length) q('credits-status').textContent = t('credits_unavailable');
      A.openDialog(dialog, trigger);
      track('credit_pack_view', { reason: reason || 'menu', logged_in: this.me.loggedIn, balance: this.me.balance });
    }

    async buyPack(id, button) {
      const pack = this.me?.packs.find((p) => p.id === String(id));
      if (!pack) return;
      const status = this.creditsDialog?.querySelector('[data-ref="credits-status"]');
      track('credit_purchase_start', { pack_credits: pack.credits, value: pack.price / 100, currency: currency(), logged_in: this.me.loggedIn });
      if (!this.me.loggedIn) {
        this.saveDraft();
        store.set('buy', pack.id);
        location.href = A.loginUrl(`${A.config.atelierUrl || '/pages/atelier'}?credits=1`);
        return;
      }
      if (!pack.variantId) { if (status) status.textContent = t('credits_unavailable'); return; }
      if (button) button.disabled = true;
      if (status) status.textContent = t('credits_redirect');
      store.set('purchase', { credits: pack.credits, price: pack.price, balanceBefore: this.me.balance, at: Date.now() });
      this.saveDraft();
      try {
        const res = await fetch(A.config.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantNumber(pack.variantId), quantity: 1 }] })
        });
        if (!res.ok) throw new Error(String(res.status));
        location.href = A.config.checkoutUrl || '/checkout';
      } catch (e) {
        store.del('purchase');
        if (status) status.textContent = t('credits_error');
        if (button) button.disabled = false;
      }
    }

    /* Retour du paiement : le solde a augmenté depuis le départ vers le checkout (webhook orders/paid traité) */
    detectPurchase(attempt = 0) {
      const p = store.get('purchase');
      if (!p || !this.me) return;
      const age = Date.now() - (p.at || 0);
      if (age > 3 * 86400000) { store.del('purchase'); return; }
      if (this.me.balance > p.balanceBefore) {
        store.del('purchase');
        track('credit_purchase_complete', { pack_credits: p.credits, value: p.price / 100, currency: currency() });
        this.showNotice(t('credits_added', { balance: this.balanceLabel() }), true);
        return;
      }
      if (age < 30 * 60000 && attempt < 3) {
        setTimeout(async () => { await this.refreshMe(); this.detectPurchase(attempt + 1); }, 5000 * (attempt + 1));
      }
    }

    /* ---------- Turnstile (uniquement si une clé publique est configurée, visiteurs non connectés) ---------- */
    prepareTurnstile() {
      if (!A.config.turnstileKey || this.me?.loggedIn || this.turnstilePromise) return;
      this.turnstilePromise = A.loadScript(TURNSTILE_SRC).then(() => {
        const box = this.refs.turnstile;
        box.hidden = false;
        this.turnstileId = window.turnstile.render(box, {
          sitekey: A.config.turnstileKey,
          action: 'atelier_generate',
          appearance: 'interaction-only',
          theme: 'dark',
          language: 'fr',
          callback: (token) => { this.turnstileValue = token; this.turnstileWaiter?.resolve(token); },
          'expired-callback': () => { this.turnstileValue = null; },
          'error-callback': () => { this.turnstileWaiter?.reject(new Error('turnstile')); }
        });
      });
      this.turnstilePromise.catch(() => { this.turnstilePromise = null; });
    }

    async turnstileToken() {
      this.prepareTurnstile();
      await this.turnstilePromise;
      if (this.turnstileValue) return this.turnstileValue;
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('timeout')), 45000);
        this.turnstileWaiter = {
          resolve: (v) => { clearTimeout(timer); resolve(v); },
          reject: (e) => { clearTimeout(timer); reject(e); }
        };
      });
    }

    resetTurnstile() {
      if (this.turnstileId === undefined || !window.turnstile) return;
      this.turnstileValue = null;
      try { window.turnstile.reset(this.turnstileId); } catch (e) { /* widget déjà retiré */ }
    }

    /* ---------- Messages ---------- */
    showAlert(message, retry) {
      const box = this.refs.alert;
      box.replaceChildren();
      const p = document.createElement('p');
      p.textContent = message;
      box.appendChild(p);
      if (retry) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--secondary button--small';
        button.textContent = A.config.retryLabel || '';
        button.addEventListener('click', () => { this.clearAlert(); retry(); }, { once: true });
        box.appendChild(button);
      }
      box.hidden = false;
    }

    clearAlert() {
      this.refs.alert.hidden = true;
      this.refs.alert.replaceChildren();
    }

    showNotice(message, success = false) {
      const box = this.refs.notice;
      box.textContent = message;
      box.classList.toggle('studio__notice--success', success);
      box.hidden = false;
    }

    hideNotice() {
      if (this.refs.notice.classList.contains('studio__notice--success')) return;
      this.refs.notice.hidden = true;
    }
  }

  /* Bouton fixe mobile : visible seulement quand ni le bouton du héros ni l'atelier ne sont à l'écran */
  class AtelierSticky extends HTMLElement {
    connectedCallback() {
      const targets = [document.getElementById('atelier-studio'), document.querySelector('[data-atelier-hero-cta]')].filter(Boolean);
      if (!targets.length || !('IntersectionObserver' in window)) return;
      const visible = new Set();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) visible.add(entry.target); else visible.delete(entry.target); });
        this.hidden = visible.size > 0;
      });
      targets.forEach((el) => this.observer.observe(el));
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }

  customElements.define('atelier-studio', AtelierStudio);
  if (!customElements.get('atelier-sticky')) customElements.define('atelier-sticky', AtelierSticky);
})();
